import {
  formatToTwoDigits,
  formatToThreeDigits,
} from "../usePanelConfig";

/**
 * Maps device type from backend format to frontend format
 * @param {string} backendType - Backend device type ("1", "2", "3", "4")
 * @returns {string} Frontend device type
 */
export const mapDeviceTypeFromBackend = (backendType) => {
  const type = String(backendType || "").toUpperCase();

  // Backend puede enviar string (DOOR/KEYPAD/ZONE_EXPANDER/CAMERA/FIRE/NONE) o códigos numéricos.
  // Frontend (select) usa: 0=None, 1=Door, 3=Keypad, 4=Zone Expander, 5=Camera/Video Doorbell, 6=Fire
  const map = {
    NONE: "0",
    "0": "0",

    DOOR: "1",
    "1": "1",

    KEYPAD: "3",
    "3": "3",

    ZONE_EXPANDER: "4",
    "4": "4",

    CAMERA: "5",
    "5": "5",

    FIRE: "6",
    "2": "6", // Backend GET devuelve 2 para FIRE
    "6": "6",
  };

  return map[type] || "0";
};

/**
 * Maps device type from frontend format to backend format
 * @param {string} frontendType - Frontend device type
 * @returns {string} Backend device type ("DOOR", "READER", "KEYPAD", "ZONE_EXPANDER")
 */
export const mapDeviceTypeToBackend = (frontendType) => {
  const typeMap = {
    "0": "NONE",
    "1": "DOOR",
    "3": "KEYPAD",
    "4": "ZONE_EXPANDER",
    "5": "CAMERA",
    "6": "FIRE",
  };
  return typeMap[frontendType] || "NONE";
};

/**
 * Maps supervision time from backend format to frontend format
 * @param {string} backendTime - Backend supervision time ("0", "2", "3")
 * @returns {string} Frontend supervision time
 */
export const mapSupervisionTimeFromBackend = (backendTime) => {
  return backendTime || "3";
};

/**
 * Maps supervision time from frontend format to backend format
 * @param {string} frontendTime - Frontend supervision time
 * @returns {string} Backend supervision time ("NONE_TIMER", "60_MINUTES", "240_MINUTES")
 */
export const mapSupervisionTimeToBackend = (frontendTime) => {
  const timeMap = {
    "0": "NONE_TIMER",
    "2": "60_MINUTES",
    "3": "240_MINUTES",
  };
  return timeMap[frontendTime] || "240_MINUTES";
};

/**
 * Maps device communication type from backend format to frontend format
 * @param {string} backendCommType - Backend comm type ("K", "W", "N")
 * @returns {string} Frontend comm type
 */
export const mapDeviceCommTypeFromBackend = (backendCommType) => {
  const comm = String(backendCommType || "").toUpperCase();

  // Backend puede enviar letra (K/W/N) o string (KEYPAD_AX_BUS/WIRELESS/NETWORK)
  const map = {
    K: "K",
    W: "W",
    N: "N",
    KEYPAD_AX_BUS: "K",
    KEYPAD_BUS: "K",
    WIRELESS: "W",
    NETWORK: "N",
  };

  return map[comm] || "K";
};

/**
 * Maps device communication type from frontend format to backend format
 * @param {string} frontendCommType - Frontend comm type
 * @returns {string} Backend comm type ("KEYPAD_AX_BUS", "WIRELESS", "NETWORK")
 */
export const mapDeviceCommTypeToBackend = (frontendCommType) => {
  const commTypeMap = {
    K: "KEYPAD_AX_BUS",
    W: "WIRELESS",
    N: "NETWORK",
  };
  return commTypeMap[frontendCommType] || "KEYPAD_AX_BUS";
};

/**
 * Maps card option from backend format to frontend format
 * @param {string} backendCardOption - Backend card option ("0", "1", "2")
 * @returns {string} Frontend card option
 */
export const mapCardOptionFromBackend = (backendCardOption) => {
  return backendCardOption || "0";
};

/**
 * Maps card option from frontend format to backend format
 * @param {string} frontendCardOption - Frontend card option
 * @returns {string} Backend card option ("DMP", "CUSTOM", "ANY")
 */
export const mapCardOptionToBackend = (frontendCardOption) => {
  const cardOptionMap = {
    "0": "DMP",
    "1": "CUSTOM",
    "2": "ANY",
  };
  return cardOptionMap[frontendCardOption] || "DMP";
};

/**
 * Maps no comm with panel from backend format to frontend format
 * @param {string} backendNoComm - Backend no comm ("0", "1", "2", "3", "4")
 * @returns {string} Frontend no comm
 */
export const mapNoCommFromBackend = (backendNoComm) => {
  return backendNoComm || "0";
};

/**
 * Maps no comm with panel from frontend format to backend format
 * @param {string} frontendNoComm - Frontend no comm
 * @returns {string} Backend no comm ("OFF", "SITE", "ANY", "ON", "LAST")
 */
export const mapNoCommToBackend = (frontendNoComm) => {
  const noCommMap = {
    "0": "OFF",
    "1": "SITE",
    "2": "ANY",
    "3": "ON",
    "4": "LAST",
  };
  return noCommMap[frontendNoComm] || "OFF";
};

/**
 * Cleans site code value (removes spaces, formats if needed)
 * @param {string} value - Site code value
 * @returns {string} Cleaned site code value or empty string
 */
export const cleanSiteCode = (value) => {
  if (!value || value.trim() === "") return "";
  const trimmed = value.trim();
  if (trimmed === "   ") return "";
  return trimmed;
};

/**
 * Formats site code for backend (3 digits or empty)
 * @param {string} value - Site code value
 * @returns {string} Formatted site code or empty string
 */
export const formatSiteCodeToBackend = (value) => {
  const cleaned = cleanSiteCode(value);
  if (!cleaned) return "";
  return formatToThreeDigits(cleaned, 1, 999);
};

/**
 * Transforms device data from backend format (GET) to frontend format
 * @param {Object} backendDevice - Device data from backend
 * @returns {Object} Device data in frontend format
 */
export const transformDeviceFromBackend = (backendDevice) => {
  const defaultExtendedConfig = {
    deviceNumber: "",
    activateZone2Bypass: "N",
    relockOnZone2Change: "N",
    zone2BypassTime: "40",
    activateZone3RequestExit: "N",
    requestExitStrikeTime: "5",
    activateOnboardSpeaker: "N",
    noCommWithPanel: "0",
    cardOption: "0",
    siteCodeLength: "8",
    wiegandCodeLength: "26",
    userCodePosition: "9",
    siteCodePosition: "1",
    userCodeLength: "16",
    requireSiteCode: "N",
    siteCode1: "127",
    siteCode2: "   ",
    siteCode3: "   ",
    siteCode4: "   ",
    siteCode5: "   ",
    siteCode6: "   ",
    siteCode7: "   ",
    siteCode8: "   ",
    numUserCodeDigits: "5",
    serialNumber: "00000000",
  };

  // Transform extended config if it exists
  let extendedConfig = { ...defaultExtendedConfig };
  if (backendDevice.extendedConfig) {
    extendedConfig = {
      ...defaultExtendedConfig,
      ...backendDevice.extendedConfig,
      // Clean site codes (convert spaces to empty strings)
      siteCode1: cleanSiteCode(backendDevice.extendedConfig.siteCode1),
      siteCode2: cleanSiteCode(backendDevice.extendedConfig.siteCode2),
      siteCode3: cleanSiteCode(backendDevice.extendedConfig.siteCode3),
      siteCode4: cleanSiteCode(backendDevice.extendedConfig.siteCode4),
      siteCode5: cleanSiteCode(backendDevice.extendedConfig.siteCode5),
      siteCode6: cleanSiteCode(backendDevice.extendedConfig.siteCode6),
      siteCode7: cleanSiteCode(backendDevice.extendedConfig.siteCode7),
      siteCode8: cleanSiteCode(backendDevice.extendedConfig.siteCode8),
    };
  }

  return {
    deviceNumber: backendDevice.deviceNumber || "",
    deviceName: backendDevice.deviceName || "",
    deviceType: mapDeviceTypeFromBackend(backendDevice.deviceType),
    deviceCommType: mapDeviceCommTypeFromBackend(backendDevice.deviceCommType),
    serialNumber: backendDevice.serialNumber || "00000000",
    supervisionTime: mapSupervisionTimeFromBackend(backendDevice.supervisionTime),
    accessAreas: backendDevice.accessAreas || "",
    egressAreas: backendDevice.egressAreas || "",
    displayAreas: backendDevice.displayAreas || "",
    strikeTime: backendDevice.strikeTime || "5",
    strikeDelay: backendDevice.strikeDelay || "0",
    fireExit: backendDevice.fireExit || "N",
    autoForceArm: backendDevice.autoForceArm || "N",
    publicDoor: backendDevice.publicDoor || "N",
    privateDoor: backendDevice.privateDoor  || "N",
    outputGroup: backendDevice.outputGroup || "N",
    doorRealTimeStatus: backendDevice.doorRealTimeStatus || "N",
    override: backendDevice.override || "N",
    sendDoorForced: backendDevice.sendDoorForced || "N",
    program734Options: backendDevice.program734Options || "N",
    extendedConfig,
  };
};

/**
 * Maps device type from frontend to the GET numeric code (template mode).
 * The GET response returns "2" for FIRE (see mapDeviceTypeFromBackend).
 */
const mapDeviceTypeToTemplate = (frontendType) => {
  const map = { "0": "0", "1": "1", "3": "3", "4": "4", "5": "5", "6": "2" };
  return map[String(frontendType)] || "0";
};

/**
 * Transforms device data from the form to the exact GET shape (template mode).
 *
 * Unlike transformDeviceToBackend (readable enums, zero-padded numbers), the
 * GET response uses short codes ("3", "K", numeric) and does NOT pad the
 * device-level numbers. The extendedConfig sub-object, however, IS zero-padded
 * in the GET response and keeps numeric cardOption / noCommWithPanel codes.
 * The original GET item is spread first so any unmodeled key is preserved.
 */
export const transformDeviceToTemplate = (frontendDevice, original = {}) => {
  const item = {
    ...original,
    deviceNumber: String(parseInt(frontendDevice.deviceNumber, 10) || 1),
    deviceName: (frontendDevice.deviceName || "").substring(0, 32).toUpperCase(),
    deviceType: mapDeviceTypeToTemplate(frontendDevice.deviceType),
    deviceCommType: frontendDevice.deviceCommType || "K",
    serialNumber: frontendDevice.serialNumber || "00000000",
    supervisionTime: frontendDevice.supervisionTime || "3",
    accessAreas: frontendDevice.accessAreas || "",
    egressAreas: frontendDevice.egressAreas || "",
    displayAreas: frontendDevice.displayAreas || "",
    strikeTime: String(parseInt(frontendDevice.strikeTime, 10) || 5),
    strikeDelay: String(parseInt(frontendDevice.strikeDelay, 10) || 0),
    fireExit: frontendDevice.fireExit || "N",
    autoForceArm: frontendDevice.autoForceArm || "N",
    publicDoor: frontendDevice.publicDoor || "N",
    privateDoor: frontendDevice.privateDoor || "N",
    outputGroup: frontendDevice.outputGroup || "N",
    doorRealTimeStatus: frontendDevice.doorRealTimeStatus || "N",
    override: frontendDevice.override || "N",
    sendDoorForced: frontendDevice.sendDoorForced || "N",
    program734Options: frontendDevice.program734Options || "N",
  };

  if (frontendDevice.program734Options === "Y" && frontendDevice.extendedConfig) {
    const ext = frontendDevice.extendedConfig;
    item.extendedConfig = {
      ...(original.extendedConfig || {}),
      activateOnboardSpeaker: ext.activateOnboardSpeaker || "N",
      activateZone2Bypass: ext.activateZone2Bypass || "N",
      activateZone3RequestExit: ext.activateZone3RequestExit || "N",
      // GET keeps the numeric codes (not the readable POST enums)
      cardOption: ext.cardOption || "0",
      deviceNumber:
        formatToTwoDigits(ext.deviceNumber || frontendDevice.deviceNumber, 1, 99) ||
        "01",
      noCommWithPanel: ext.noCommWithPanel || "0",
      numUserCodeDigits: ext.numUserCodeDigits || "05",
      relockOnZone2Change: ext.relockOnZone2Change || "N",
      requestExitStrikeTime:
        formatToThreeDigits(ext.requestExitStrikeTime, 5, 250) || "005",
      requireSiteCode: ext.requireSiteCode || "N",
      siteCode1: formatSiteCodeToBackend(ext.siteCode1),
      siteCode2: formatSiteCodeToBackend(ext.siteCode2),
      siteCode3: formatSiteCodeToBackend(ext.siteCode3),
      siteCode4: formatSiteCodeToBackend(ext.siteCode4),
      siteCode5: formatSiteCodeToBackend(ext.siteCode5),
      siteCode6: formatSiteCodeToBackend(ext.siteCode6),
      siteCode7: formatSiteCodeToBackend(ext.siteCode7),
      siteCode8: formatSiteCodeToBackend(ext.siteCode8),
      siteCodeLength: formatToTwoDigits(ext.siteCodeLength, 1, 16) || "08",
      siteCodePosition: formatToThreeDigits(ext.siteCodePosition, 0, 255) || "001",
      userCodeLength: formatToTwoDigits(ext.userCodeLength, 1, 64) || "16",
      userCodePosition: formatToThreeDigits(ext.userCodePosition, 0, 255) || "009",
      wiegandCodeLength:
        formatToThreeDigits(ext.wiegandCodeLength, 1, 255) || "026",
      zone2BypassTime: formatToThreeDigits(ext.zone2BypassTime, 20, 250) || "040",
    };
  }

  return item;
};

/**
 * Transforms device data from frontend format to backend format (POST)
 * @param {Object} frontendDevice - Device data from frontend form
 * @returns {Object} Device data in backend format
 */
export const transformDeviceToBackend = (frontendDevice) => {
  const payload = {
    deviceNumber: formatToTwoDigits(frontendDevice.deviceNumber, 1, 99) || "01",
    deviceType: mapDeviceTypeToBackend(frontendDevice.deviceType),
    supervisionTime: mapSupervisionTimeToBackend(frontendDevice.supervisionTime),
    deviceName: (frontendDevice.deviceName || "").substring(0, 32).toUpperCase(),
    deviceCommType: mapDeviceCommTypeToBackend(frontendDevice.deviceCommType),
    serialNumber: frontendDevice.serialNumber || "00000000",
    accessAreas: frontendDevice.accessAreas || "",
    egressAreas: frontendDevice.egressAreas || "",
    displayAreas: frontendDevice.displayAreas || "",
    strikeTime: formatToThreeDigits(frontendDevice.strikeTime, 1, 250) || "005",
    strikeDelay: frontendDevice.strikeDelay || "0",
    fireExit: frontendDevice.fireExit || "N",
    autoForceArm: frontendDevice.autoForceArm || "N",
    publicDoor: frontendDevice.publicDoor || "N",
    privateDoor: frontendDevice.privateDoor || "N",
    outputGroup: frontendDevice.outputGroup || "N",
    doorRealTimeStatus: frontendDevice.doorRealTimeStatus || "N",
    override: frontendDevice.override || "N",
    sendDoorForced: frontendDevice.sendDoorForced || "N",
    program734Options: frontendDevice.program734Options || "N",
  };

  // Only include extendedConfig if program734Options is "Y"
  if (frontendDevice.program734Options === "Y" && frontendDevice.extendedConfig) {
    const ext = frontendDevice.extendedConfig;
    payload.extendedConfig = {
      activateOnboardSpeaker: ext.activateOnboardSpeaker || "N",
      activateZone2Bypass: ext.activateZone2Bypass || "N",
      activateZone3RequestExit: ext.activateZone3RequestExit || "N",
      cardOption: mapCardOptionToBackend(ext.cardOption),
      deviceNumber: formatToTwoDigits(ext.deviceNumber || frontendDevice.deviceNumber, 1, 99) || "01",
      noCommWithPanel: mapNoCommToBackend(ext.noCommWithPanel),
      numUserCodeDigits: ext.numUserCodeDigits || "05",
      relockOnZone2Change: ext.relockOnZone2Change || "N",
      requestExitStrikeTime: formatToThreeDigits(ext.requestExitStrikeTime, 5, 250) || "005",
      requireSiteCode: ext.requireSiteCode || "N",
      siteCode1: formatSiteCodeToBackend(ext.siteCode1),
      siteCode2: formatSiteCodeToBackend(ext.siteCode2),
      siteCode3: formatSiteCodeToBackend(ext.siteCode3),
      siteCode4: formatSiteCodeToBackend(ext.siteCode4),
      siteCode5: formatSiteCodeToBackend(ext.siteCode5),
      siteCode6: formatSiteCodeToBackend(ext.siteCode6),
      siteCode7: formatSiteCodeToBackend(ext.siteCode7),
      siteCode8: formatSiteCodeToBackend(ext.siteCode8),
      siteCodeLength: formatToTwoDigits(ext.siteCodeLength, 1, 16) || "08",
      siteCodePosition: formatToThreeDigits(ext.siteCodePosition, 0, 255) || "001",
      userCodeLength: formatToTwoDigits(ext.userCodeLength, 1, 64) || "16",
      userCodePosition: formatToThreeDigits(ext.userCodePosition, 0, 255) || "009",
      wiegandCodeLength: formatToThreeDigits(ext.wiegandCodeLength, 1, 255) || "026",
      zone2BypassTime: formatToThreeDigits(ext.zone2BypassTime, 20, 250) || "040",
    };
  }

  return payload;
};
